import React, { useState } from "react";
import { MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { formatApiDetail } from "../../lib/api";
import {
  downloadPacketByInvoiceId,
  sendPacket,
} from "../../lib/packetApi";

export default function InvoiceRowActions({ row, onEdit, onSendReminder }) {
  const status = (row?.status || "").toUpperCase();
  const isPaid = status === "PAID";
  const isDraft = status === "DRAFT" || Boolean(row?.isDraft);
  const canRemind = !isPaid && !isDraft;
  const canEdit = !isPaid;
  const hasPacket = Boolean(row?.libraryFileId || row?.id);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleEmail = async () => {
    if (!hasPacket || busy) return;
    setBusy(true);
    try {
      const data = await sendPacket({
        invoiceId: row.id,
        fileId: row.libraryFileId,
      });
      toast.success(
        data?.to_email ? `Packet emailed to ${data.to_email}` : "Packet emailed",
      );
      setOpen(false);
    } catch (e) {
      toast.error(formatApiDetail(e.response?.data?.detail, "Could not email packet."));
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    if (!hasPacket || busy) return;
    setBusy(true);
    try {
      if (row.id) {
        await downloadPacketByInvoiceId(row.id, `${row.invoiceNumber || "invoice"}.pdf`);
      } else if (row.libraryFileId) {
        const { downloadPacketByFileId } = await import("../../lib/packetApi");
        await downloadPacketByFileId(row.libraryFileId, `${row.invoiceNumber || "invoice"}.pdf`);
      } else {
        throw Object.assign(new Error("No PDF linked to this invoice"), {
          response: { data: { detail: "No PDF linked to this invoice" } },
        });
      }
      toast.success("PDF downloaded");
      setOpen(false);
    } catch (e) {
      // Prefer invoice path; if that fails and we have a library file, try direct file download.
      if (row.id && row.libraryFileId) {
        try {
          const { downloadPacketByFileId } = await import("../../lib/packetApi");
          await downloadPacketByFileId(row.libraryFileId, `${row.invoiceNumber || "invoice"}.pdf`);
          toast.success("PDF downloaded");
          setOpen(false);
          return;
        } catch {
          // fall through to original error
        }
      }
      toast.error(formatApiDetail(e.response?.data?.detail || e.message, "Could not download PDF."));
    } finally {
      setBusy(false);
    }
  };

  const itemClass =
    "rounded-[var(--t-event-pill-radius)] px-3 py-2 text-left text-[10px] font-normal uppercase tracking-[0.16em] transition-colors hover:bg-black/[0.04] disabled:pointer-events-none disabled:opacity-40";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-9 w-9 items-center justify-center rounded-[var(--t-event-pill-radius)] border border-transparent text-[var(--t-meta)] hover:border-[var(--t-border)] hover:text-[var(--t-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--t-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--t-bg)]"
          onClick={(e) => e.stopPropagation()}
          aria-label={`Actions for ${row.invoiceNumber}`}
          data-testid={`invoice-actions-${row.invoiceNumber}`}
        >
          <MoreHorizontal size={16} strokeWidth={1.5} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="z-[60] w-auto min-w-[12rem] neo-panel p-2 text-[var(--t-text)] t-glass"
        data-testid={`invoice-actions-menu-${row.invoiceNumber}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-1">
          <button
            type="button"
            className={itemClass}
            onClick={() => {
              setOpen(false);
              onEdit?.(row);
            }}
            data-testid={`invoice-action-edit-${row.invoiceNumber}`}
          >
            {canEdit ? "Edit invoice" : "View invoice"}
          </button>
          <button
            type="button"
            disabled={!hasPacket || busy}
            className={itemClass}
            onClick={() => {
              if (hasPacket) handleEmail();
            }}
            data-testid={`invoice-action-email-${row.invoiceNumber}`}
          >
            Email PDF
          </button>
          <button
            type="button"
            disabled={!hasPacket || busy}
            className={itemClass}
            onClick={() => {
              if (hasPacket) handleDownload();
            }}
            data-testid={`invoice-action-download-${row.invoiceNumber}`}
          >
            Download PDF
          </button>
          {canRemind ? (
            <button
              type="button"
              className={itemClass}
              onClick={() => {
                setOpen(false);
                onSendReminder?.(row);
              }}
              data-testid={`invoice-action-reminder-${row.invoiceNumber}`}
            >
              Send reminder
            </button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
